import { makeAuthenticatedApiRequest, ApiResult, ApiError, ApiEndpoint } from './apiConfig';
import { validateAccessToken, validateCategoryName } from './validators';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
    'https://s54yjn65k4.execute-api.us-east-1.amazonaws.com/v1';

export const KEYWORD_ENDPOINTS = {
    STORE_MAPPING: {
        url: `${API_BASE_URL}/keywords`,
        method: 'POST'
    } as ApiEndpoint,
    GET_MAPPINGS: {
        url: `${API_BASE_URL}/keywords`,
        method: 'GET'
    } as ApiEndpoint,
    DELETE_MAPPING: (keyword: string) => ({
        url: `${API_BASE_URL}/keywords/${encodeURIComponent(keyword)}`,
        method: 'DELETE'
    } as ApiEndpoint)
};

// Request/Response Types
export interface StoreKeywordMappingRequest {
    keyword: string;
    category: string;
}

export interface StoreKeywordMappingResponse {
    message: string;
    mapping: KeywordMappingItem;
}

export interface GetKeywordMappingsResponse {
    mappings: KeywordMappingItem[];
    count: number;
}

export interface DeleteKeywordMappingResponse {
    message: string;
    keyword: string;
}

export interface KeywordMappingItem {
    PK: string;
    SK: string;
    keyword: string;
    category: string;
    createdAt: string;
    updatedAt: string;
}

// Service Functions

/**
 * Store a keyword-to-category mapping
 * @param keyword - Keyword phrase to map (will be converted to lowercase)
 * @param category - Category name to map the keyword to
 * @param accessToken - JWT access token for authentication
 * @returns API result with the stored keyword mapping
 */
export async function storeKeywordMapping(
    keyword: string,
    category: string,
    accessToken: string
): Promise<ApiResult<StoreKeywordMappingResponse>> {
    validateAccessToken(accessToken);
    validateKeyword(keyword);
    validateCategoryName(category);

    const request: StoreKeywordMappingRequest = {
        keyword: keyword.trim(),
        category: category.trim().toLowerCase()
    };

    return makeAuthenticatedApiRequest<StoreKeywordMappingResponse>(
        KEYWORD_ENDPOINTS.STORE_MAPPING,
        request,
        accessToken
    );
}

/**
 * Get all keyword mappings for the authenticated user
 * @param accessToken - JWT access token for authentication
 * @returns API result with array of keyword mappings
 */
export async function getKeywordMappings(
    accessToken: string
): Promise<ApiResult<GetKeywordMappingsResponse>> {
    validateAccessToken(accessToken);

    return makeAuthenticatedApiRequest<GetKeywordMappingsResponse>(
        KEYWORD_ENDPOINTS.GET_MAPPINGS,
        undefined,
        accessToken
    );
}

/**
 * Delete a keyword mapping by keyword phrase
 * @param keyword - Keyword phrase to delete (case-insensitive)
 * @param accessToken - JWT access token for authentication
 * @returns API result with deletion confirmation
 */
export async function deleteKeywordMapping(
    keyword: string,
    accessToken: string
): Promise<ApiResult<DeleteKeywordMappingResponse>> {
    validateAccessToken(accessToken);
    validateKeyword(keyword);

    const endpoint = KEYWORD_ENDPOINTS.DELETE_MAPPING(keyword);

    return makeAuthenticatedApiRequest<DeleteKeywordMappingResponse>(
        endpoint,
        undefined,
        accessToken
    );
}

/**
 * Validate keyword format
 * @param keyword - Keyword to validate
 * @throws ApiError if keyword is invalid
 */
function validateKeyword(keyword: unknown): asserts keyword is string {
    if (typeof keyword !== 'string') {
        throw new ApiError('Keyword must be a string');
    }

    const trimmed = keyword.trim();

    if (trimmed.length === 0) {
        throw new ApiError('Keyword cannot be empty');
    }

    if (trimmed.length > 200) {
        throw new ApiError('Keyword cannot exceed 200 characters');
    }
}

/**
 * Find matching keyword mappings for a transaction description
 * Useful for local keyword-based categorization before API calls
 * @param description - Transaction description to search
 * @param mappings - Array of keyword mappings to search through
 * @returns Matching category or undefined if no match found
 */
export function findMatchingKeywordCategory(
    description: string,
    mappings: KeywordMappingItem[]
): string | undefined {
    if (!description || !mappings.length) {
        return undefined;
    }

    const lowerDescription = description.toLowerCase();

    // Sort by keyword length (longest first) to match more specific keywords first
    const sortedMappings = [...mappings].sort(
        (a, b) => b.keyword.length - a.keyword.length
    );

    for (const mapping of sortedMappings) {
        if (lowerDescription.includes(mapping.keyword.toLowerCase())) {
            console.log("Keyword found locally...");
            return mapping.category;
        }
    }

    return undefined;
}
